import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Chip,
    Grid,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Pagination
} from "@mui/material";
import { EditOutlined, DeleteOutline } from "@mui/icons-material";
import {
    collection,
    getDocs,
    doc,
    setDoc,
    deleteDoc,
    query,
    where,
    Timestamp
} from "firebase/firestore";
import {
    createUserWithEmailAndPassword,
    getAuth,
    onAuthStateChanged,
    signInWithEmailAndPassword
} from "firebase/auth";
import { db } from "../../firebase";
import Swal from "sweetalert2";
import Menu from "../../panel/Menu";

const PAGE_SIZE = 50;

const ROLE_OPTIONS = [
    "Restoadmin",
    "Counter",
    "Captain",
    "Chef",
];

const STATUS_OPTIONS = [
    "Active",
    "Deactivated",
    "Disabled",
];

function Restousers() {

    const auth = getAuth();

    const [propertyId, setPropertyId] = useState(null);
    const [loginemail, setLoginEmail] = useState("");
    const [loginpass, setLoginPass] = useState("");

    const [users, setUsers] = useState([]);
    const [editingId, setEditingId] = useState(null);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const [form, setForm] = useState({
        name: "",
        email: "",
        mobile: "",
        password: "",
        role: "Captain",
        status: "Active",
        type: "Resto"
    });

    /* ================= AUTH CHECK ================= */

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) return;

            const snap = await getDocs(collection(db, "authroles"));
            snap.forEach(d => {
                if (d.id === user.uid) {
                    setPropertyId(d.data().propertyId);
                    setLoginEmail(d.data().email);
                    setLoginPass(d.data().password);

                    // Force logout if not Active
                    if (d.data().status !== "Active") {
                        Swal.fire("Access Blocked", "Your account is disabled", "error");
                        auth.signOut();
                    }
                }
            });
        });

        return () => unsub();
    }, []);

    /* ================= LOAD USERS ================= */

    useEffect(() => {
        if (!propertyId) return;
        loadUsers();
    }, [propertyId]);

    const loadUsers = async () => {
        const q = query(
            collection(db, "authroles"),
            where("propertyId", "==", propertyId),
            where("type", "==", "Resto")
        );

        const snap = await getDocs(q);
        setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    /* ================= FORM HANDLER ================= */

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    /* ================= SAVE USER ================= */

    const saveUser = async () => {
        try {

            if (!form.name || !form.email) {
                return Swal.fire("Error", "Name and Email required", "error");
            }

            // ===== UPDATE USER =====
            if (editingId) {

                await setDoc(
                    doc(db, "authroles", editingId),
                    {
                        ...form,
                        propertyId,
                        updatedTime: Timestamp.now()
                    },
                    { merge: true }
                );

                Swal.fire("Success", "User updated", "success");

            } else {

                if (!form.password) {
                    return Swal.fire("Error", "Password required", "error");
                }

                // ===== CREATE NEW AUTH USER =====
                const userCredential = await createUserWithEmailAndPassword(
                    auth,
                    form.email,
                    form.password
                );

                const user = userCredential.user;

                await setDoc(doc(db, "authroles", user.uid), {
                    uid: user.uid,
                    ...form,
                    propertyId,
                    createdTime: Timestamp.now()
                });

                // Restore admin session
                await auth.signOut();
                await signInWithEmailAndPassword(auth, loginemail, loginpass);

                Swal.fire("Success", "User added", "success");
            }

            setForm({
                name: "",
                email: "",
                mobile: "",
                password: "",
                role: "Captain",
                status: "Active",
                type: "Resto"
            });

            setEditingId(null);
            loadUsers();

        } catch (error) {
            Swal.fire("Error", error.message, "error");
        }
    };

    /* ================= EDIT USER ================= */

    const editUser = (u) => {
        setForm({
            name: u.name,
            email: u.email,
            mobile: u.mobile,
            password: "",
            role: u.role,
            status: u.status,
            type: "Resto"
        });
        setEditingId(u.uid);
    };

    /* ================= DELETE USER ================= */

    const deleteUser = async (uid) => {

        const res = await Swal.fire({
            title: "Delete user?",
            text: "This will remove access",
            icon: "warning",
            showCancelButton: true
        });

        if (!res.isConfirmed) return;

        await deleteDoc(doc(db, "authroles", uid));

        Swal.fire("Deleted", "User removed", "success");
        loadUsers();
    };

    /* ================= FILTER + PAGINATION ================= */

    const filtered = users.filter(u =>
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
    );

    const paginated = filtered.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
    );

    /* ================= UI ================= */

    return (
        <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
            <Menu />
            <Box flex={1} p={3}>

                <Card>
                    <CardContent>

                        <Typography variant="h5" fontWeight={800} mb={2}>
                            Resto User Logins
                        </Typography>

                        {/* FORM */}
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography fontWeight={700} mb={2}>
                                    {editingId ? "Edit User" : "Add New User"}
                                </Typography>

                                <Grid container spacing={2}>

                                    <Grid item xs={12} md={3}>
                                        <TextField fullWidth size="small" label="Name" name="name" value={form.name} onChange={handleChange} />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <TextField fullWidth size="small" label="Email" name="email" value={form.email} onChange={handleChange} />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <TextField fullWidth size="small" label="Mobile" name="mobile" value={form.mobile} onChange={handleChange} />
                                    </Grid>

                                    {!editingId && (
                                        <Grid item xs={12} md={3}>
                                            <TextField fullWidth size="small" label="Password" name="password" value={form.password} onChange={handleChange} />
                                        </Grid>
                                    )}

                                    <Grid item xs={12} md={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Role</InputLabel>
                                            <Select name="role" value={form.role} onChange={handleChange}>
                                                {ROLE_OPTIONS.map(r => (
                                                    <MenuItem key={r} value={r}>{r}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Status</InputLabel>
                                            <Select name="status" value={form.status} onChange={handleChange}>
                                                {STATUS_OPTIONS.map(s => (
                                                    <MenuItem key={s} value={s}>{s}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Button variant="contained" sx={{ mt: 2 }} onClick={saveUser}>
                                    {editingId ? "Update User" : "Add User"}
                                </Button>
                            </CardContent>
                        </Card>

                        {/* SEARCH */}
                        <TextField
                            label="Search by name or email"
                            size="small"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            sx={{ mb: 2 }}
                        />

                        {/* TABLE */}
                        <Card>
                            <CardContent>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Sr No</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Mobile</TableCell>
                                            <TableCell>Role</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginated.map((u, index) => (
                                            <TableRow key={u.uid}>
                                                <TableCell>
                                                    {(page - 1) * PAGE_SIZE + index + 1}
                                                </TableCell>
                                                <TableCell>{u.name}</TableCell>
                                                <TableCell>{u.email}</TableCell>
                                                <TableCell>{u.mobile}</TableCell>
                                                <TableCell>{u.role}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={u.status}
                                                        color={u.status === "Active" ? "success" : "default"}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton onClick={() => editUser(u)}>
                                                        <EditOutlined color="primary" />
                                                    </IconButton>
                                                    <IconButton onClick={() => deleteUser(u.uid)}>
                                                        <DeleteOutline color="error" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>

                                <Box display="flex" justifyContent="center" mt={2}>
                                    <Pagination
                                        count={Math.ceil(filtered.length / PAGE_SIZE)}
                                        page={page}
                                        onChange={(e, v) => setPage(v)}
                                    />
                                </Box>
                            </CardContent>
                        </Card>

                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}

export default Restousers;